/**
 * Utilidades para el módulo de Calendario
 */

import {
  formatDateInput,
  buildIsoFromDateInput,
  getServiceDayNumber,
  getBusinessDateParts,
  getServiceYearMonth,
  isFutureBusinessMonth,
  isOvernightHour,
} from "../../utils/dateUtils";

export { formatDateInput, buildIsoFromDateInput };

export const isInternalCalendarEvent = (item) =>
  Boolean(item) && item._isFromServices !== true && Boolean(item.eventType);

const isAutoRecurrenceCopy = (item) =>
  Boolean(
    item?.autoGenerated ||
      item?.recurrenceSource ||
      item?._repeatedFrom
  );

/**
 * Previsualización fantasma = copia recurrente de un mes futuro.
 * Pasa a servicio real el 1.er día operativo de ese mes (5:00 AM, hora México).
 */
export const isGhostOccurrence = (item) => {
  if (!item) return false;
  if (item._isVirtual || item._isGhost) return true;
  if (!isAutoRecurrenceCopy(item)) return false;

  const iso = item.isoDate || item.date;
  const ym = getServiceYearMonth(iso);
  if (!ym) return false;

  return isFutureBusinessMonth(ym.year, ym.month);
};

export const getGhostTransitionLabel = (item) => {
  const ym = getServiceYearMonth(item?.isoDate || item?.date);
  if (!ym) return "Al iniciar el mes destino";
  const label = new Date(Date.UTC(ym.year, ym.month - 1, 1)).toLocaleDateString(
    "es-MX",
    { month: "long", year: "numeric", timeZone: "UTC" }
  );
  return `Será editable desde el 1.º día operativo de ${label} (5:00 AM)`;
};

/**
 * Convierte hora 24h a formato 12h (AM/PM)
 */
export const to12Hour = (time24) => {
  if (!time24) return "";
  const [hourStr, minute] = time24.split(":");
  let hour = parseInt(hourStr, 10);
  if (Number.isNaN(hour)) return time24;
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12;
  if (hour === 0) hour = 12;
  return `${hour}:${minute || "00"} ${ampm}`;
};

/**
 * Calcula los días del calendario para un mes dado
 */
export const getCalendarDays = (currentDate) => {
  const totalDays = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0
  ).getDate();

  const firstDay = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  ).getDay();

  const offset = firstDay === 0 ? 6 : firstDay - 1;

  return Array.from({ length: 42 }, (_, i) => {
    const dayNumber = i - offset + 1;
    return dayNumber > 0 && dayNumber <= totalDays ? dayNumber : null;
  });
};

/**
 * Obtiene el nombre del mes formateado
 */
export const getMonthName = (date, locale = "es-MX") => {
  return date
    .toLocaleDateString(locale, { month: "long", year: "numeric" })
    .toUpperCase();
};

/**
 * Verifica si una fecha es el día operativo actual (cambia a las 5:00 AM)
 */
/**
 * Antes de las 5:00 el servicio pertenece al día operativo anterior
 * y no se vuelve a mostrar en su fecha de calendario.
 */
export const eventBelongsToCalendarDay = (event, day) => {
  if (!event || !day) return false;
  if (isOvernightHour(event.hour)) return event.date === day + 1;
  return event.date === day;
};

export const isToday = (day, currentDate) => {
  const today = getBusinessDateParts();
  return (
    day === today.day &&
    currentDate.getMonth() === today.month &&
    currentDate.getFullYear() === today.year
  );
};

/**
 * Normaliza eventos del calendario para incluir día y fecha ISO.
 *
 * FIX: usaba getDate() (timezone local) para extraer el número de día.
 * En Aguascalientes (UTC-6), un evento guardado como "2025-05-26T00:00:00Z"
 * se interpretaba como "2025-05-25T18:00:00" local → getDate() devolvía 25.
 * Resultado: eventos del día 26 aparecían en el día 25 del calendario,
 * y el filtro "hasta hoy" incluía eventos futuros inflando el denominador.
 *
 * Solución: getUTCDate() extrae el día directamente en UTC, sin conversión
 * de timezone, igual que como lo guardó el backend.
 */
export const normalizeCalendarEvents = (services) => {
  const result = [];

  for (const service of services || []) {
    if (!service.date) continue;

    const dayNumber = getServiceDayNumber(service.date);
    if (!dayNumber) continue;

    const technicians =
      service.technicians?.length > 0
        ? service.technicians
        : service.technician
        ? [service.technician]
        : [];

    // Sin técnicos
    if (technicians.length === 0) {
      result.push({
        ...service,
        date: dayNumber,
        isoDate: service.date,
        technician: null,
        originalServiceId: service._id,
      });

      continue;
    }

    // Un evento virtual por técnico
    technicians.forEach((tech) => {
      result.push({
        ...service,
        date: dayNumber,
        isoDate: service.date,
        technician: tech,
        originalServiceId: service._id,
      });
    });
  }

  return result;
};